import { useState } from "react"
import { MenuItemType } from "./utils"
import { useAuthenticatedUser } from "auth-provider"
import useUserStore from "stores/user"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "components/ui/collapsible"
import {
  Sidebar,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarProvider,
  SidebarContent,
} from "components/ui/sidebar"
import { ScrollArea, ScrollBar } from "components/ui/scroll-area"
import { Button } from "components/ui/button"
import { GripIcon, ChevronRight } from "lucide-react"
import { useLocation, Link } from "react-router-dom"

interface AppMenuProps {
  menuItems: MenuItemType[]
}

const isMenuItemVisible = (
  item: MenuItemType,
  {
    isDriver,
    isCustomer,
    isExternal,
    isCommonCarrier,
    isCC,
    userGroups,
  }: {
    isDriver: boolean
    isCustomer: boolean
    isExternal: boolean
    isCommonCarrier: boolean
    isCC: boolean
    userGroups?: string[]
  }
) => {
  if (
    item.hide ||
    (isDriver && !item.showDriver) ||
    (isCustomer && !item.showForCustomer) ||
    (isExternal && item.hideForExternal) ||
    (isCommonCarrier && item.hideForCC) ||
    (isCC && item.hideForAnyCC) ||
    (item.adminOnly && !userGroups?.includes("Admin"))
  ) {
    return false
  }
  return true
}

const AppMenu = ({ menuItems }: AppMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { pathname } = useLocation()
  const { user } = useAuthenticatedUser()

  const isCommonCarrier = useUserStore((state) => state.isCommonCarrier)
  const isCC = useUserStore((state) => state.isCC)
  const isCustomer = useUserStore((state) => state.isCustomer)
  const isExternal = useUserStore((state) => state.isExternal)
  const isDriver = useUserStore((state) => state.isDriver)

  const userFlags = {
    isDriver,
    isCustomer,
    isExternal,
    isCommonCarrier,
    isCC,
    userGroups: user?.groups,
  }

  const handleItemClick = (itemAction?: () => void) => () => {
    setIsOpen(false)
    itemAction?.()
  }

  const renderMenuItems = (items: MenuItemType[]) => {
    return items
      .filter((item) => isMenuItemVisible(item, userFlags))
      .map((item) => {
        const isActive =
          pathname === item.url ||
          item.children?.some((child) => pathname === child.url)

        if (item.children) {
          return (
            <ul className="list-none">
              <Collapsible
                key={item.key}
                asChild
                defaultOpen={isActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={item.label}>
                      {item.icon && <item.icon />}
                      <span>{item.label}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.children
                        .filter((child) => isMenuItemVisible(child, userFlags))
                        .map((child) => (
                          <SidebarMenuSubItem key={child.key}>
                            <SidebarMenuSubButton
                              asChild
                              onClick={handleItemClick(child?.onClick)}
                              className={
                                pathname === child?.url
                                  ? "bg-sidebar-accent"
                                  : ""
                              }
                            >
                              {child.url ? (
                                <Link to={child.url}>
                                  <span>{child.label}</span>
                                </Link>
                              ) : (
                                <span>{child.label}</span>
                              )}
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </ul>
          )
        }

        return (
          <ul className="list-none">
            <SidebarMenuItem key={item.key}>
              {item.url ? (
                <SidebarMenuButton
                  tooltip={item.label}
                  asChild
                  onClick={handleItemClick(item?.onClick)}
                  className={pathname === item?.url ? "bg-sidebar-accent" : ""}
                >
                  <Link to={item.url}>
                    {item.icon && <item.icon />}
                    <span>{item.label}</span>
                  </Link>
                </SidebarMenuButton>
              ) : (
                <SidebarMenuButton
                  tooltip={item.label}
                  onClick={handleItemClick(item?.onClick)}
                  className={pathname === item?.url ? "bg-sidebar-accent" : ""}
                >
                  {item.icon && <item.icon />}
                  <span>{item.label}</span>
                </SidebarMenuButton>
              )}
            </SidebarMenuItem>
          </ul>
        )
      })
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="focus:ring-transparent focus:ring-0 focus-visible:ring-0"
          aria-label="Toggle sidebar"
        >
          <GripIcon className="w-6 h-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="dark text-white p-4 rounded-lg">
        <SidebarProvider className="min-h-[calc(98vh-8rem)] h-[calc(98vh-8rem)]">
          <ScrollArea thumbClassName="bg-zinc-700">
            <Sidebar collapsible="none" variant="floating">
              <SidebarContent>{renderMenuItems(menuItems)}</SidebarContent>
            </Sidebar>
            <ScrollBar />
          </ScrollArea>
        </SidebarProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { AppMenu }
